// ============================================================================
// Module Analysis: Flexible & Customizable Real-Time Analytics Dashboard
// Authoritative Real-Time Aggregator, No-Code Builder & Interactive Grid
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart3,
  Plus,
  HelpCircle,
  AlertCircle,
  Info,
  ChevronDown,
  ChevronUp,
  Sparkles,
} from 'lucide-react';
import {
  CustomAnalysisDashboard,
  CustomAnalysisWidget,
  CreateWidgetDTO,
  UpdateWidgetDTO,
  AnalysisQueryFilter,
  DataSourceCatalogItem,
} from './types';
import { analysisService } from './analysisService';
import { AnalysisFilterToolbar } from './components/AnalysisFilterToolbar';
import { AnalysisWidgetCard } from './components/AnalysisWidgetCard';
import { AnalysisWidgetBuilderModal } from './components/AnalysisWidgetBuilderModal';
import { AnalysisDrilldownModal } from './components/AnalysisDrilldownModal';

export const AnalysisPage: React.FC = () => {
  const [dashboards, setDashboards] = useState<CustomAnalysisDashboard[]>([]);
  const [selectedDashboardId, setSelectedDashboardId] = useState<string>('');
  const [activeDashboard, setActiveDashboard] = useState<CustomAnalysisDashboard | null>(null);
  const [catalog, setCatalog] = useState<DataSourceCatalogItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Layout / Edit Mode
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  // Global Filters & Interactive Cross-Filters
  const [filters, setFilters] = useState<AnalysisQueryFilter>({
    dateRange: 'all',
    severity: 'ALL',
    department: 'ALL',
  });

  // Modal State for Visual Builder
  const [isWidgetModalOpen, setIsWidgetModalOpen] = useState<boolean>(false);
  const [editingWidget, setEditingWidget] = useState<CustomAnalysisWidget | null>(null);

  // Modal State for Drilldown (Opening Reports & Records)
  const [isDrilldownOpen, setIsDrilldownOpen] = useState<boolean>(false);
  const [drilldownWidget, setDrilldownWidget] = useState<CustomAnalysisWidget | null>(null);
  const [drilldownDimensionValue, setDrilldownDimensionValue] = useState<string | undefined>(undefined);

  // User Guide Banner Toggle
  const [showGuide, setShowGuide] = useState<boolean>(false);

  // Drag and Drop State
  const [draggedWidgetId, setDraggedWidgetId] = useState<string | null>(null);

  // 1. Initial Load: Dashboards & Catalog
  const loadInitialData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [dashList, catList] = await Promise.all([
        analysisService.getDashboards(),
        analysisService.getCatalog(),
      ]);

      setDashboards(dashList);
      setCatalog(catList);

      if (dashList.length > 0) {
        const toSelect = selectedDashboardId && dashList.some((d) => d.id === selectedDashboardId)
          ? selectedDashboardId
          : dashList[0].id;
        setSelectedDashboardId(toSelect);
        await loadDashboardDetails(toSelect);
      }
      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard metadata');
      setLoading(false);
    }
  }, [selectedDashboardId]);

  useEffect(() => {
    loadInitialData();
  }, []);

  // 2. Load Single Dashboard Details with Widgets
  const loadDashboardDetails = async (dashboardId: string) => {
    try {
      const dash = await analysisService.getDashboardById(dashboardId);
      setActiveDashboard(dash);
    } catch (err: any) {
      console.error('Failed to load dashboard:', err);
    }
  };

  // Switch Dashboard
  const handleSelectDashboard = async (id: string) => {
    setSelectedDashboardId(id);
    setFilters((prev) => ({ ...prev, crossFilters: undefined }));
    await loadDashboardDetails(id);
  };

  // Create Dashboard
  const handleCreateDashboard = async (title: string, description?: string) => {
    try {
      const created = await analysisService.createDashboard({ title, description });
      const updatedList = await analysisService.getDashboards();
      setDashboards(updatedList);
      setSelectedDashboardId(created.id);
      await loadDashboardDetails(created.id);
    } catch (err: any) {
      alert(`Failed to create dashboard: ${err.message}`);
    }
  };

  // Delete Dashboard
  const handleDeleteDashboard = async (id: string) => {
    try {
      await analysisService.deleteDashboard(id);
      const updatedList = await analysisService.getDashboards();
      setDashboards(updatedList);
      if (updatedList.length > 0) {
        setSelectedDashboardId(updatedList[0].id);
        await loadDashboardDetails(updatedList[0].id);
      } else {
        setActiveDashboard(null);
      }
    } catch (err: any) {
      alert(`Failed to delete dashboard: ${err.message}`);
    }
  };

  // Save Widget (Create or Update)
  const handleSaveWidget = async (dto: CreateWidgetDTO | UpdateWidgetDTO) => {
    if (!selectedDashboardId) return;

    try {
      if (editingWidget) {
        await analysisService.updateWidget(editingWidget.id, dto as UpdateWidgetDTO);
      } else {
        await analysisService.createWidget(selectedDashboardId, dto as CreateWidgetDTO);
      }
      await loadDashboardDetails(selectedDashboardId);
      setEditingWidget(null);
    } catch (err: any) {
      alert(`Failed to save widget: ${err.message}`);
    }
  };

  // Delete Widget
  const handleDeleteWidget = async (widgetId: string) => {
    if (!confirm('Are you sure you want to delete this analytics widget?')) return;
    try {
      await analysisService.deleteWidget(widgetId);
      await loadDashboardDetails(selectedDashboardId);
    } catch (err: any) {
      alert(`Failed to delete widget: ${err.message}`);
    }
  };

  // Resize Widget Width (Toggle 4 -> 6 -> 8 -> 12 -> 4)
  const handleResizeWidget = async (widgetId: string, newWidth: number) => {
    try {
      await analysisService.updateWidget(widgetId, { widthCol: newWidth });
      await loadDashboardDetails(selectedDashboardId);
    } catch (err: any) {
      console.error('Resize failed:', err);
    }
  };

  // Cross-Filter: Clicking a chart element dynamically filters other widgets!
  const handleCrossFilter = (dimension: string, value: string) => {
    setFilters((prev) => {
      const current = prev.crossFilters || {};
      if (current[dimension] === value) {
        const next = { ...current };
        delete next[dimension];
        return {
          ...prev,
          crossFilters: Object.keys(next).length > 0 ? next : undefined,
        };
      } else {
        return {
          ...prev,
          crossFilters: {
            ...current,
            [dimension]: value,
          },
        };
      }
    });
  };

  // Open Drilldown Modal (To Inspect Underlying Reports & Records)
  const handleOpenDrilldown = (widget: CustomAnalysisWidget, dimensionValue?: string) => {
    setDrilldownWidget(widget);
    setDrilldownDimensionValue(dimensionValue);
    setIsDrilldownOpen(true);
  };

  // HTML5 Drag and Drop Handlers for Reordering
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, widgetId: string) => {
    setDraggedWidgetId(widgetId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>, targetWidgetId: string) => {
    e.preventDefault();
    if (!draggedWidgetId || draggedWidgetId === targetWidgetId || !activeDashboard?.widgets) return;

    const widgets = [...activeDashboard.widgets];
    const fromIndex = widgets.findIndex((w) => w.id === draggedWidgetId);
    const toIndex = widgets.findIndex((w) => w.id === targetWidgetId);

    if (fromIndex < 0 || toIndex < 0) return;

    const [moved] = widgets.splice(fromIndex, 1);
    widgets.splice(toIndex, 0, moved);

    setActiveDashboard({ ...activeDashboard, widgets });
    setDraggedWidgetId(null);

    const newOrderIds = widgets.map((w) => w.id);
    try {
      await analysisService.reorderWidgets(activeDashboard.id, newOrderIds);
    } catch (err) {
      console.error('Failed to save layout order:', err);
    }
  };

  const widgetsList = activeDashboard?.widgets || [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Top Banner Guide Toggle */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
        <button
          type="button"
          onClick={() => setShowGuide(!showGuide)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            borderRadius: '8px',
            border: '1px solid #cbd5e1',
            backgroundColor: '#ffffff',
            color: '#475569',
            fontSize: '12px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          <HelpCircle size={14} color="#2563eb" />
          <span>Dashboard Guide {showGuide ? <ChevronUp size={12} /> : <ChevronDown size={12} />}</span>
        </button>
      </div>

      {/* User Guide Onboarding Banner */}
      {showGuide && (
        <div
          style={{
            backgroundColor: '#ffffff',
            border: '1px solid #dbeafe',
            borderLeft: '4px solid #2563eb',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '16px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#1d4ed8', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Info size={16} /> How to Use Custom Analysis Dashboards (4 Easy Steps)
            </span>
            <button
              type="button"
              onClick={() => setShowGuide(false)}
              style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '12px' }}
            >
              Close ✕
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', marginTop: '10px' }}>
            <div style={{ fontSize: '12px', color: '#334155' }}>
              <strong style={{ color: '#0f172a', display: 'block', marginBottom: '3px' }}>1. Boards & Templates:</strong>
              Select pre-configured flight safety or quality boards from the top dropdown, or create a new board with <strong>"+ New Dashboard"</strong>.
            </div>
            <div style={{ fontSize: '12px', color: '#334155' }}>
              <strong style={{ color: '#0f172a', display: 'block', marginBottom: '3px' }}>2. Widget Creation:</strong>
              Click <strong>"+ Add Widget"</strong> to choose your data source (Occurrences, Reports, Audits, Actions), dimension axes, and visual chart type.
            </div>
            <div style={{ fontSize: '12px', color: '#334155' }}>
              <strong style={{ color: '#0f172a', display: 'block', marginBottom: '3px' }}>3. Drill Down to Records:</strong>
              Click <strong>"Drill Down"</strong> on any card or click on a chart bar/slice to view underlying reports and audit records.
            </div>
            <div style={{ fontSize: '12px', color: '#334155' }}>
              <strong style={{ color: '#0f172a', display: 'block', marginBottom: '3px' }}>4. Drag & Drop Layout:</strong>
              Click <strong>"Customize Layout"</strong> to drag cards into your preferred sequence and resize columns (4, 6, 8, 12 cols).
            </div>
          </div>
        </div>
      )}

      {/* Filter & Dashboard Management Toolbar */}
      <AnalysisFilterToolbar
        dashboards={dashboards}
        selectedDashboardId={selectedDashboardId}
        onSelectDashboard={handleSelectDashboard}
        onCreateDashboard={handleCreateDashboard}
        onDeleteDashboard={handleDeleteDashboard}
        onOpenAddWidget={() => {
          setEditingWidget(null);
          setIsWidgetModalOpen(true);
        }}
        isEditMode={isEditMode}
        onToggleEditMode={() => setIsEditMode(!isEditMode)}
        filters={filters}
        onFilterChange={setFilters}
        onClearCrossFilters={() => setFilters((prev) => ({ ...prev, crossFilters: undefined }))}
        onRefresh={() => selectedDashboardId && loadDashboardDetails(selectedDashboardId)}
        loading={loading}
      />

      {/* Main Content Area */}
      {error ? (
        <div style={{ padding: '36px', textAlign: 'center', backgroundColor: '#ffffff', borderRadius: '12px', border: '1px solid #fecaca', margin: '20px 0' }}>
          <AlertCircle size={36} color="#dc2626" style={{ margin: '0 auto 8px auto' }} />
          <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>Data Loading Error</h3>
          <p style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>{error}</p>
        </div>
      ) : widgetsList.length === 0 ? (
        /* Empty Dashboard State */
        <div
          style={{
            padding: '48px',
            textAlign: 'center',
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            border: '2px dashed #cbd5e1',
            margin: '24px auto',
            maxWidth: '540px',
          }}
        >
          <div
            style={{
              width: '56px',
              height: '56px',
              backgroundColor: '#eff6ff',
              color: '#2563eb',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px auto',
            }}
          >
            <Sparkles size={28} />
          </div>
          <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>No Widgets on this Dashboard Yet</h3>
          <p style={{ fontSize: '12px', color: '#64748b', marginTop: '6px', marginBottom: '24px', lineHeight: 1.5 }}>
            Add your first custom visual widget. Aggregate and visualize occurrences, safety reports, audit findings, or CAPA progression.
          </p>
          <button
            type="button"
            onClick={() => {
              setEditingWidget(null);
              setIsWidgetModalOpen(true);
            }}
            className="btn-primary"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              width: 'auto',
              padding: '10px 20px',
              margin: '0 auto',
            }}
          >
            <Plus size={16} /> Add First Widget
          </button>
        </div>
      ) : (
        /* Guaranteed Responsive 12-Column CSS Grid */
        <div
          className="analysis-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
            gap: '20px',
            width: '100%',
          }}
        >
          {widgetsList.map((widget) => (
            <AnalysisWidgetCard
              key={widget.id}
              widget={widget}
              filters={filters}
              isEditMode={isEditMode}
              onEditWidget={(w) => {
                setEditingWidget(w);
                setIsWidgetModalOpen(true);
              }}
              onDeleteWidget={handleDeleteWidget}
              onResizeWidget={handleResizeWidget}
              onCrossFilter={handleCrossFilter}
              onOpenDrilldown={handleOpenDrilldown}
              activeCrossFilterValue={filters.crossFilters?.[widget.dimension]}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            />
          ))}
        </div>
      )}

      {/* Visual No-Code Widget Builder Modal */}
      <AnalysisWidgetBuilderModal
        isOpen={isWidgetModalOpen}
        onClose={() => {
          setIsWidgetModalOpen(false);
          setEditingWidget(null);
        }}
        onSave={handleSaveWidget}
        catalog={catalog}
        dashboardId={selectedDashboardId}
        initialWidget={editingWidget}
      />

      {/* Drill-down Modal (Opens Detailed Reports & Underlying Records) */}
      {drilldownWidget && (
        <AnalysisDrilldownModal
          isOpen={isDrilldownOpen}
          onClose={() => {
            setIsDrilldownOpen(false);
            setDrilldownWidget(null);
          }}
          widgetTitle={drilldownWidget.title}
          dataSource={drilldownWidget.dataSource}
          dimension={drilldownWidget.dimension}
          dimensionValue={drilldownDimensionValue}
          filters={filters}
        />
      )}
    </div>
  );
};

export default AnalysisPage;
